import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { seoVitals, seoRunVitals } from "@/lib/seo/seo-os.functions";
import { SeoPage, Stat, Panel, Pill, Loading, Empty, DataTable, num } from "@/components/admin/seo/SeoUi";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/seo/web-vitals")({
  head: () => ({ meta: [{ title: "Page speed · Admin" }, { name: "robots", content: "noindex, nofollow" }] }),
  component: VitalsPage,
});

function verdict(row: any) {
  const bad = (row.lcp_ms ?? 0) > 4000 || (row.cls ?? 0) > 0.25 || (row.inp_ms ?? 0) > 500;
  const warn = (row.lcp_ms ?? 0) > 2500 || (row.cls ?? 0) > 0.1 || (row.inp_ms ?? 0) > 200;
  return bad ? { label: "poor", tone: "bad" } : warn ? { label: "needs work", tone: "warn" } : { label: "good", tone: "good" };
}

function VitalsPage() {
  const load = useServerFn(seoVitals);
  const run = useServerFn(seoRunVitals);
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["seo-vitals"], queryFn: () => load() });
  const d = q.data as any;
  const rows = (d?.rows ?? []) as any[];

  const sweep = useMutation({
    mutationFn: () => run({ data: { baseUrl: typeof window !== "undefined" ? window.location.origin : undefined } }),
    onSuccess: (r: any) => {
      toast.success(`Measured ${r.measured} page views${r.failed ? `, ${r.failed} failed` : ""}.`);
      qc.invalidateQueries({ queryKey: ["seo-vitals"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Measurement failed."),
  });

  return (
    <SeoPage
      title="Page speed"
      description="How fast your key pages load and respond on phones and desktops, measured by Google. Slow pages lose both rankings and enquiries."
      actions={
        <Button onClick={() => sweep.mutate()} disabled={sweep.isPending}>
          {sweep.isPending ? "Measuring…" : "Measure now"}
        </Button>
      }
    >
      {q.isLoading ? (
        <Loading />
      ) : rows.length === 0 ? (
        <Empty>{d?.detail ?? "No measurements yet."}</Empty>
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-3">
            <Stat label="Pages measured" value={num(new Set(rows.map((r) => r.path)).size)} />
            <Stat label="Good" value={num(rows.filter((r) => verdict(r).label === "good").length)} />
            <Stat label="Needs work or poor" value={num(rows.filter((r) => verdict(r).label !== "good").length)} />
          </div>
          <Panel title="Latest measurements" subtitle={d?.detail ?? ""}>
            <DataTable
              head={["Page", "Device", "Score", "Load (LCP)", "Shift (CLS)", "Response (INP)", "Verdict"]}
              rows={rows.map((r) => {
                const v = verdict(r);
                return [
                  r.path,
                  r.strategy,
                  r.performance_score ?? "—",
                  r.lcp_ms ? `${r.lcp_ms} ms` : "—",
                  r.cls ?? "—",
                  r.inp_ms ? `${r.inp_ms} ms` : "—",
                  <Pill key={`${r.path}${r.strategy}`} tone={v.tone}>
                    {v.label}
                  </Pill>,
                ];
              })}
            />
          </Panel>
        </>
      )}
    </SeoPage>
  );
}
